import { Injector } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { TConfigurationFormModelTypes } from '../../../../models/form-builder-types-configurations.model';
import {
  baseFieldParseIntoSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
  getPopulateRelativeToRootFieldConfig,
  getPopulatesPropsSettingsConfigFormly,
} from '../../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { FormBuilderService } from '../../../../services/form-builder.service';
import { BaseFormlyFieldConfiguration } from './base-formly-field-configuration';

export interface IAdvancedFieldDefinition {
  propKeys: string[];
  fields: FormlyFieldConfig[];
  stringArrayPaths?: string[];
  recordPaths?: string[];
  stripEmptyValuePaths?: string[];
}

const input = (
  key: string,
  label: string,
  options: Record<string, unknown> = {}
): FormlyFieldConfig => ({
  key,
  type: EIremboFormlyFieldTypes.input,
  props: {
    label,
    placeholder: `Enter ${label.toLowerCase()}`,
    ...options,
  },
});

const textarea = (key: string, label: string): FormlyFieldConfig => ({
  key,
  type: EIremboFormlyFieldTypes.textarea,
  props: { label, placeholder: `Enter ${label.toLowerCase()}`, rows: 3 },
});

const toggle = (
  key: string,
  label: string,
  description?: string
): FormlyFieldConfig => ({
  key,
  type: EIremboFormlyFieldTypes.checkbox,
  props: { label, description, formCheck: 'switch' },
});

const select = (
  key: string,
  label: string,
  values: Array<string | { label: string; value: unknown }>
): FormlyFieldConfig => ({
  key,
  type: EIremboFormlyFieldTypes.customdropdown,
  props: {
    label,
    placeholder: `Select ${label.toLowerCase()}`,
    bindLabel: 'label',
    bindValue: 'value',
    options: values.map(value =>
      typeof value === 'string' ? { label: value, value } : value
    ),
  },
});

const group = (
  label: string,
  fields: FormlyFieldConfig[],
  key?: string
): FormlyFieldConfig => ({
  key,
  wrappers: ['panel-wrapper'],
  props: { label },
  fieldGroup: fields,
});

const repeater = (
  key: string,
  label: string,
  fields: FormlyFieldConfig[],
  titleFieldName = 'key'
): FormlyFieldConfig => ({
  key,
  type: EIremboFormlyFieldTypes.customexpansionrepeater,
  wrappers: ['custom-expansion-panel-with-icon'],
  defaultValue: [],
  props: {
    label,
    hideLabel: true,
    addText: `Add ${label.replace(/s$/, '')}`,
    titleFieldName,
  },
  fieldArray: {
    type: EIremboFormlyFieldTypes['formly-group'],
    fieldGroup: fields,
  },
});

const formFieldSelect = (
  key: string,
  label: string,
  formBuilderService: FormBuilderService
): FormlyFieldConfig => ({
  key,
  type: EIremboFormlyFieldTypes.customdropdown,
  props: {
    label,
    placeholder: `Select ${label.toLowerCase()}`,
    options: formBuilderService.getAllFormFieldLabelAndDetails(true, false),
    bindLabel: 'label',
    bindValue: 'value',
  },
});

const payloadMappings = (
  key: string,
  label: string,
  formBuilderService: FormBuilderService
): FormlyFieldConfig =>
  repeater(
    key,
    label,
    [
      input('key', 'Payload Key', { required: true }),
      toggle('isStatic', 'Use Static Value'),
      input('staticValue', 'Static Value'),
      formFieldSelect('sourceField', 'Source Field', formBuilderService),
      toggle('searchInRootForm', 'Search In Root Form'),
      input('defaultValue', 'Default Value'),
    ],
    'key'
  );

const directParamMappings = (
  key: string,
  label: string,
  formBuilderService: FormBuilderService
): FormlyFieldConfig =>
  repeater(
    key,
    label,
    [
      input('key', 'Parameter Key', { required: true }),
      input('value', 'Static Value'),
      formFieldSelect('sourceField', 'Source Field', formBuilderService),
      toggle('searchInRootForm', 'Search In Root Form'),
      input('defaultValue', 'Default Value'),
    ],
    'key'
  );

const recordEntries = (
  key: string,
  label: string,
  keyLabel = 'Key',
  valueLabel = 'Value'
): FormlyFieldConfig =>
  repeater(key, label, [
    input('key', keyLabel, { required: true }),
    input('value', valueLabel, { required: true }),
  ]);

const populationFields = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig[] => [
  getPopulateRelativeToRootFieldConfig(),
  ...getPopulatesPropsSettingsConfigFormly(formBuilderService, []),
];

const errorMappingFields = (): FormlyFieldConfig =>
  group(
    'Error Mapping',
    [
      input('defaultErrorCode', 'Default Error Code'),
      select('detectionType', 'Detection Type', ['simple', 'advanced']),
      input('errorMessagePath', 'Error Message Path'),
      toggle('useApiMessage', 'Use API Message'),
      input('errorCode', 'Error Code'),
      input('errorMessage', 'Custom Error Message'),
    ],
    'errorMapping'
  );

const genericFetchFields = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig[] => [
  group('Request', [
    input('url', 'API URL'),
    input('endpointCode', 'Endpoint Code'),
    toggle('useBaseUrl', 'Use API Gateway Base URL'),
    select('triggerType', 'Trigger Type', [
      'INPUT_LENGTH',
      'TRIGGER_BUTTON',
      'FIELD_LISTENER',
      'ON_SUCCESS',
    ]),
    input('debounceTime', 'Debounce Time (ms)', { type: 'number', min: 0 }),
    input('genericInputLength', 'Input Length', { type: 'number', min: 0 }),
    toggle('inputLenghtAsMinLength', 'Use Input Length As Minimum'),
    toggle('inputLenghtAsMaxLength', 'Use Input Length As Maximum'),
    toggle('numericOnly', 'Numbers Only'),
    toggle('disableInternalFormControl', 'Disable Internal Input'),
    toggle('showGlobalError', 'Show Error Globally'),
    input('successMessage', 'Success Message'),
  ]),
  group('Payload', [
    input('payloadKey', 'Payload Key'),
    toggle('useMultiplePayloadKeys', 'Use Multiple Payload Keys'),
    payloadMappings(
      'payloadKeyMappings',
      'Payload Mappings',
      formBuilderService
    ),
  ]),
  group('Populate Fields', populationFields(formBuilderService)),
  errorMappingFields(),
];

const datasetFields = (
  includeUrl: boolean,
  includePagination: boolean
): FormlyFieldConfig =>
  group(
    'Dataset',
    [
      select('httpMethod', 'HTTP Method', ['GET', 'POST']),
      ...(includeUrl ? [input('url', 'URL')] : []),
      input('dataField', 'Response Data Path'),
      ...(includePagination
        ? [
            input('searchKey', 'Search Parameter'),
            input('pageKey', 'Page Parameter'),
            input('sizeKey', 'Page Size Parameter'),
            input('pageSize', 'Page Size', { type: 'number', min: 1 }),
            input('pageStart', 'First Page', { type: 'number', min: 0 }),
          ]
        : []),
      recordEntries('params', 'Request Parameters', 'Parameter', 'Value'),
      recordEntries('headers', 'Request Headers', 'Header', 'Value'),
    ],
    'dataset'
  );

const columnFields = (withInputType = false): FormlyFieldConfig[] => [
  input('key', 'Column Key', { required: true }),
  input('label', 'Column Label'),
  input('title', 'Column Title'),
  ...(withInputType
    ? [
        select('type', 'Input Type', [
          'text',
          'number',
          'email',
          'date',
          'select',
        ]),
      ]
    : []),
  input('dataPath', 'Data Path'),
  input('formatter', 'Formatter'),
  input('placeholder', 'Placeholder'),
  input('width', 'Width'),
  toggle('sortable', 'Sortable'),
  toggle('required', 'Required'),
  toggle('disabled', 'Disabled'),
];

const editableTableColumnFields = (): FormlyFieldConfig[] => [
  input('key', 'Column Key', { required: true }),
  input('label', 'Column Label', { required: true }),
  select('type', 'Input Type', ['text', 'number', 'email', 'date', 'select']),
  input('placeholder', 'Placeholder'),
  repeater(
    'options',
    'Select Options',
    [
      input('label', 'Option Label', { required: true }),
      input('value', 'Option Value', { required: true }),
    ],
    'label'
  ),
  input('min', 'Minimum Value', { type: 'number' }),
  input('max', 'Maximum Value', { type: 'number' }),
  input('pattern', 'Pattern'),
  input('width', 'Width'),
  toggle('required', 'Required'),
  toggle('disabled', 'Disabled'),
];

const preFetchChecks = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig =>
  repeater('preFetchChecks', 'Pre-fetch Checks', [
    toggle('enabled', 'Enabled'),
    input('url', 'URL'),
    input('endpointCode', 'Endpoint Code'),
    toggle('useBaseUrl', 'Use API Gateway Base URL'),
    select('method', 'HTTP Method', ['GET', 'POST']),
    input('errorKey', 'Error Key'),
    input('errorMessage', 'Error Message'),
    input('errorMessagePath', 'Error Message Path'),
    toggle('useMultiplePayloadKeys', 'Use Multiple Payload Keys'),
    payloadMappings(
      'payloadKeyMappings',
      'Payload Mappings',
      formBuilderService
    ),
  ]);

// Exported so the table-with-input column editor can render the same
// type-specific settings for a column as a standalone field of that type
// gets, instead of maintaining a second copy of them.
export const definitions = (
  formBuilderService: FormBuilderService
): Partial<Record<EIremboFormlyFieldTypes, IAdvancedFieldDefinition>> => ({
  [EIremboFormlyFieldTypes.customlabelvaluerepeater]: {
    propKeys: ['description', 'addText'],
    fields: [
      group('Repeater', [
        textarea('description', 'Description'),
        input('addText', 'Add Button Text'),
      ]),
    ],
  },
  [EIremboFormlyFieldTypes.customconditionaldatafetch]: {
    propKeys: [
      'url',
      'endpointCode',
      'useBaseUrl',
      'triggerType',
      'debounceTime',
      'genericInputLength',
      'inputLenghtAsMinLength',
      'inputLenghtAsMaxLength',
      'numericOnly',
      'disableInternalFormControl',
      'showGlobalError',
      'successMessage',
      'payloadKey',
      'useMultiplePayloadKeys',
      'payloadKeyMappings',
      'populateRelativeToRootForm',
      'populates',
      'errorMapping',
      'onSuccessOf',
      'fetchCondition',
    ],
    fields: [
      ...genericFetchFields(formBuilderService),
      group('Fetch Dependency', [
        formFieldSelect('onSuccessOf', 'Run After Field', formBuilderService),
        group(
          'Condition',
          [
            formFieldSelect('field', 'Condition Field', formBuilderService),
            select('operator', 'Comparator', [
              'EQ',
              'NEQ',
              'GT',
              'GTE',
              'LT',
              'LTE',
              'CONTAINS',
            ]),
            input('value', 'Expected Value'),
            toggle('searchInRootForm', 'Search In Root Form'),
          ],
          'fetchCondition'
        ),
      ]),
    ],
  },
  [EIremboFormlyFieldTypes.customimage]: {
    propKeys: [
      'imageSource',
      'imageSourceType',
      'width',
      'tabletWidth',
      'mobileWidth',
      'objectFit',
      'borderRadius',
      'alt',
    ],
    fields: [
      group('Image', [
        select('imageSourceType', 'Image Source', ['FETCHED_DATA']),
        input('imageSource', 'Fetched Data Path'),
        input('width', 'Desktop Width'),
        input('tabletWidth', 'Tablet Width'),
        input('mobileWidth', 'Mobile Width'),
        select('objectFit', 'Object Fit', [
          'contain',
          'cover',
          'fill',
          'none',
          'scale-down',
        ]),
        input('borderRadius', 'Border Radius'),
        input('alt', 'Alternative Text'),
      ]),
    ],
  },
  [EIremboFormlyFieldTypes.genericbuttonfetch]: {
    propKeys: [
      'buttonText',
      'buttonStyle',
      'width',
      'alignment',
      'disabled',
      'isClickRequired',
      'checkType',
      'url',
      'endpointCode',
      'useBaseUrl',
      'payloadKey',
      'useMultiplePayloadKeys',
      'payloadKeyMappings',
      'successMessageAfterFetch',
      'showGlobalError',
      'populateRelativeToRootForm',
      'populates',
      'errorMapping',
    ],
    fields: [
      group('Button', [
        input('buttonText', 'Button Text'),
        select('buttonStyle', 'Button Style', [
          'primary',
          'secondary',
          'outline-primary',
          'danger',
        ]),
        select('alignment', 'Alignment', ['left', 'center', 'right']),
        input('width', 'Width'),
        toggle('disabled', 'Disabled'),
        toggle('isClickRequired', 'Require Button Click'),
        select('checkType', 'Recheck When', ['MANUAL', 'FIELD_LISTENER']),
        input('successMessageAfterFetch', 'Success Message'),
      ]),
      group('Request', [
        input('url', 'API URL'),
        input('endpointCode', 'Endpoint Code'),
        toggle('useBaseUrl', 'Use API Gateway Base URL'),
        input('payloadKey', 'Payload Key'),
        toggle('useMultiplePayloadKeys', 'Use Multiple Payload Keys'),
        payloadMappings(
          'payloadKeyMappings',
          'Payload Mappings',
          formBuilderService
        ),
        toggle('showGlobalError', 'Show Error Globally'),
      ]),
      group('Populate Fields', populationFields(formBuilderService)),
      errorMappingFields(),
    ],
  },
  [EIremboFormlyFieldTypes.customeditableinput]: {
    propKeys: [
      'inputType',
      'textType',
      'minNumber',
      'maxNumber',
      'minLength',
      'maxLength',
      'pattern',
      'dateFormat',
      'delimiter',
      'addRemoveTimeMinDate',
      'addRemoveTimeMaxDate',
      'initialValue',
      'minDate',
      'maxDate',
      'options',
      'bindLabel',
      'bindValue',
      'multiple',
      'dataset',
      'useBaseUrl',
      'populateRelativeToRootForm',
      'populates',
    ],
    fields: [
      group('Editable Input', [
        select('inputType', 'Input Type', [
          'text',
          'number',
          'date',
          'time',
          'dropdown',
        ]),
        select('textType', 'Text Type', ['text', 'email', 'tel', 'password']),
        input('minNumber', 'Minimum Number', { type: 'number' }),
        input('maxNumber', 'Maximum Number', { type: 'number' }),
        input('minLength', 'Minimum Length', { type: 'number', min: 0 }),
        input('maxLength', 'Maximum Length', { type: 'number', min: 0 }),
        input('pattern', 'Pattern'),
        input('dateFormat', 'Date Format'),
        input('delimiter', 'Date Delimiter'),
        input('addRemoveTimeMinDate', 'Minimum Date Offset'),
        input('addRemoveTimeMaxDate', 'Maximum Date Offset'),
        input('initialValue', 'Initial Value'),
        input('bindLabel', 'Option Label Key'),
        input('bindValue', 'Option Value Key'),
        toggle('multiple', 'Allow Multiple Values'),
        toggle('useBaseUrl', 'Use API Gateway Base URL'),
      ]),
      group(
        'Minimum Date',
        [
          input('year', 'Year', { type: 'number' }),
          input('month', 'Month', { type: 'number', min: 1, max: 12 }),
          input('day', 'Day', { type: 'number', min: 1, max: 31 }),
        ],
        'minDate'
      ),
      group(
        'Maximum Date',
        [
          input('year', 'Year', { type: 'number' }),
          input('month', 'Month', { type: 'number', min: 1, max: 12 }),
          input('day', 'Day', { type: 'number', min: 1, max: 31 }),
        ],
        'maxDate'
      ),
      repeater(
        'options',
        'Options',
        [
          input('label', 'Label', { required: true }),
          input('value', 'Value', { required: true }),
        ],
        'label'
      ),
      datasetFields(true, false),
      group('Populate Fields', populationFields(formBuilderService)),
    ],
    recordPaths: ['dataset.params', 'dataset.headers'],
  },
  [EIremboFormlyFieldTypes.customcascadingeditableinput]: {
    propKeys: [
      'description',
      'linear',
      'cascadingConfigs',
      'initializedLocationKeys',
      'populateRelativeToRootForm',
      'populates',
    ],
    fields: [
      group('Cascade', [
        textarea('description', 'Description'),
        toggle('linear', 'Require Sequential Selection'),
      ]),
      repeater('cascadingConfigs', 'Cascade Levels', [
        input('key', 'Key', { required: true }),
        input('label', 'Label', { required: true }),
        input('placeholder', 'Placeholder'),
        input('parent', 'Parent Key'),
        input('parentBindKey', 'Parent Value Key'),
        input('bindlabel', 'Display Key'),
        input('bindvalue', 'Value Key'),
        toggle('required', 'Required'),
        toggle('multiple', 'Allow Multiple Values'),
        input('min', 'Minimum Selections', { type: 'number', min: 0 }),
        input('max', 'Maximum Selections', { type: 'number', min: 0 }),
        group(
          'Dataset',
          [
            input('url', 'URL'),
            input('dataField', 'Response Data Path'),
            toggle('useBaseUrl', 'Use API Gateway Base URL'),
          ],
          'dataset'
        ),
      ]),
      recordEntries(
        'initializedLocationKeys',
        'Initialized Location Keys',
        'Cascade Key',
        'Form Field Key'
      ),
      group('Populate Fields', populationFields(formBuilderService)),
    ],
    recordPaths: ['initializedLocationKeys'],
  },
  [EIremboFormlyFieldTypes.customselfieverification]: {
    propKeys: [
      'buttonText',
      'descriptionText',
      'maxAttempts',
      'useDetectFaceEndpoint',
      'detectFaceEndpoint',
      'phoneNumbersKey',
      'legalGuardianIdKey',
      'preEnrollmentTempIdKey',
      'phoneNumberSelectionTitle',
      'phoneInputPlaceholder',
      'phoneInputPattern',
      'phoneInputOnlyCountries',
      'phoneInputCountryNoValidation',
      'phoneNumberRequiredMessage',
      'verificationCodeSentSuccessMessage',
      'cameraNotAvailableMessage',
      'citizenPhotoNotAvailableMessage',
      'faceVerificationErrorMessage',
      'customErrorMessages',
      'isButtonDisabled',
    ],
    stringArrayPaths: ['phoneInputOnlyCountries'],
    fields: [
      group('Selfie Verification', [
        input('buttonText', 'Button Text'),
        textarea('descriptionText', 'Description'),
        input('maxAttempts', 'Maximum Attempts', { type: 'number', min: 1 }),
        toggle('useDetectFaceEndpoint', 'Use Face Detection Endpoint'),
        input('detectFaceEndpoint', 'Face Detection Endpoint'),
        formFieldSelect(
          'phoneNumbersKey',
          'Phone Numbers Field',
          formBuilderService
        ),
        formFieldSelect(
          'legalGuardianIdKey',
          'Guardian ID Field',
          formBuilderService
        ),
        formFieldSelect(
          'preEnrollmentTempIdKey',
          'Pre-enrollment ID Field',
          formBuilderService
        ),
        input('phoneNumberSelectionTitle', 'Phone Selection Title'),
        input('phoneInputPlaceholder', 'Phone Placeholder'),
        input('phoneInputPattern', 'Phone Pattern'),
        input('phoneInputOnlyCountries', 'Allowed Countries', {
          description: 'Comma-separated ISO country codes',
        }),
        toggle('phoneInputCountryNoValidation', 'Skip Country Validation'),
        toggle('isButtonDisabled', 'Disable Verification Button'),
      ]),
      group('Messages', [
        input('phoneNumberRequiredMessage', 'Phone Required Message'),
        input('verificationCodeSentSuccessMessage', 'Code Sent Message'),
        input('cameraNotAvailableMessage', 'Camera Unavailable Message'),
        input('citizenPhotoNotAvailableMessage', 'Photo Unavailable Message'),
        input('faceVerificationErrorMessage', 'Verification Error Message'),
      ]),
      recordEntries(
        'customErrorMessages',
        'Custom Error Messages',
        'Error Key',
        'Message'
      ),
    ],
    recordPaths: ['customErrorMessages'],
  },
  [EIremboFormlyFieldTypes.customparagraph]: {
    propKeys: ['paragraphText', 'showLoader'],
    fields: [
      group('Paragraph', [
        textarea('paragraphText', 'Paragraph Text'),
        toggle('showLoader', 'Show Loading Indicator'),
      ]),
    ],
  },
  [EIremboFormlyFieldTypes.customeditabletable]: {
    propKeys: [
      'rows',
      'columns',
      'customTableClass',
      'showRowLabels',
      'rowColumnTitle',
      'allowRowAddition',
      'allowRowDeletion',
      'disabled',
    ],
    fields: [
      group('Table', [
        input('customTableClass', 'Table CSS Class'),
        toggle('showRowLabels', 'Show Row Labels'),
        input('rowColumnTitle', 'Row Column Title'),
        toggle('allowRowAddition', 'Allow Row Addition'),
        toggle('allowRowDeletion', 'Allow Row Deletion'),
        toggle('disabled', 'Disabled'),
      ]),
      repeater(
        'rows',
        'Rows',
        [
          input('key', 'Row Key', { required: true }),
          input('label', 'Row Label', { required: true }),
          toggle('disabled', 'Disabled'),
        ],
        'label'
      ),
      repeater('columns', 'Columns', editableTableColumnFields(), 'label'),
    ],
  },
  [EIremboFormlyFieldTypes.customdropdowndatafetchpaginated]: {
    propKeys: [
      'url',
      'endpointCode',
      'useBaseUrl',
      'dataset',
      'bindLabel',
      'bindValue',
      'multiple',
      'virtualScroll',
      'min',
      'max',
      'debounceTime',
      'triggerType',
      'showGlobalError',
      'populateRelativeToRootForm',
      'populates',
      'errorMapping',
    ],
    fields: [
      group('Dropdown', [
        input('url', 'API URL'),
        input('endpointCode', 'Endpoint Code'),
        toggle('useBaseUrl', 'Use API Gateway Base URL'),
        input('bindLabel', 'Display Key'),
        input('bindValue', 'Value Key'),
        toggle('multiple', 'Allow Multiple Values'),
        toggle('virtualScroll', 'Enable Virtual Scrolling'),
        input('min', 'Minimum Selections', { type: 'number', min: 0 }),
        input('max', 'Maximum Selections', { type: 'number', min: 0 }),
        input('debounceTime', 'Debounce Time (ms)', { type: 'number', min: 0 }),
        select('triggerType', 'Trigger Type', ['SEARCH', 'FIELD_LISTENER']),
        toggle('showGlobalError', 'Show Error Globally'),
      ]),
      datasetFields(false, true),
      errorMappingFields(),
      group('Populate Fields', populationFields(formBuilderService)),
    ],
    recordPaths: ['dataset.params', 'dataset.headers'],
  },
  [EIremboFormlyFieldTypes.externalfileupload]: {
    propKeys: [
      'endpointCode',
      'deleteEndpointCode',
      'fileFieldName',
      'payloadMapping',
      'deletePayloadMapping',
      'validation',
      'attachmentCode',
      'isCertificate',
      'validatePhoto',
      'disableDuplicateValidation',
      'sessionKeepAlive',
      'populateRelativeToRootForm',
      'populates',
    ],
    stringArrayPaths: ['validation.allowedFormats'],
    fields: [
      group('External Upload', [
        input('endpointCode', 'Upload Endpoint Code'),
        input('deleteEndpointCode', 'Delete Endpoint Code'),
        input('fileFieldName', 'Multipart File Field Name'),
        input('attachmentCode', 'Attachment Code'),
        toggle('isCertificate', 'Certificate Upload'),
        toggle('validatePhoto', 'Validate Photo'),
        toggle('disableDuplicateValidation', 'Allow Duplicate Files'),
      ]),
      group(
        'Session Keep Alive',
        [
          toggle('enableKeepAlive', 'Enable Session Keep Alive'),
          input('keepAliveInterval', 'Refresh Interval (ms)', {
            type: 'number',
            min: 1000,
          }),
        ],
        'sessionKeepAlive'
      ),
      group(
        'File Validation',
        [
          input('allowedFormats', 'Allowed Formats', {
            description: 'Comma-separated extensions, for example PDF,JPG,PNG',
          }),
          input('minimumUploadSize', 'Minimum Size (KB)', {
            type: 'number',
            min: 0,
          }),
          input('maximumUploadSize', 'Maximum Size (KB)', {
            type: 'number',
            min: 0,
          }),
        ],
        'validation'
      ),
      payloadMappings(
        'payloadMapping',
        'Upload Payload Mappings',
        formBuilderService
      ),
      payloadMappings(
        'deletePayloadMapping',
        'Delete Payload Mappings',
        formBuilderService
      ),
      group('Populate Fields', populationFields(formBuilderService)),
    ],
  },
  [EIremboFormlyFieldTypes.custominternalfileupload]: {
    propKeys: [
      'validation',
      'attachmentCode',
      'isCertificate',
      'validatePhoto',
      'disableDuplicateValidation',
      'sessionKeepAlive',
      'populateRelativeToRootForm',
    ],
    stringArrayPaths: ['validation.allowedFormats'],
    fields: [
      group('Internal Upload', [
        input('attachmentCode', 'Attachment Code'),
        toggle('isCertificate', 'Certificate Upload'),
        toggle('validatePhoto', 'Validate Photo'),
        toggle('disableDuplicateValidation', 'Allow Duplicate Files'),
        getPopulateRelativeToRootFieldConfig(),
      ]),
      group(
        'Session Keep Alive',
        [
          toggle('enableKeepAlive', 'Enable Session Keep Alive'),
          input('keepAliveInterval', 'Refresh Interval (ms)', {
            type: 'number',
            min: 1000,
          }),
        ],
        'sessionKeepAlive'
      ),
      group(
        'File Validation',
        [
          input('allowedFormats', 'Allowed Formats', {
            description: 'Comma-separated extensions, for example PDF,JPG,PNG',
          }),
          input('minimumUploadSize', 'Minimum Size (KB)', {
            type: 'number',
            min: 0,
          }),
          input('maximumUploadSize', 'Maximum Size (KB)', {
            type: 'number',
            min: 0,
          }),
        ],
        'validation'
      ),
    ],
  },
  [EIremboFormlyFieldTypes.customitemselectiontable]: {
    propKeys: [
      'title',
      'emptyMessage',
      'searchable',
      'searchPlaceHolder',
      'customTableClass',
      'disabled',
      'columns',
      'data',
      'sourceField',
      'selectionConfig',
      'url',
      'endpointCode',
      'useBaseUrl',
      'dataset',
      'debounceTime',
      'errorMapping',
    ],
    fields: [
      group('Selection Table', [
        input('title', 'Title'),
        input('emptyMessage', 'Empty Message'),
        toggle('searchable', 'Searchable'),
        input('searchPlaceHolder', 'Search Placeholder'),
        input('customTableClass', 'Table CSS Class'),
        toggle('disabled', 'Disabled'),
        formFieldSelect(
          'sourceField',
          'Source Array Field',
          formBuilderService
        ),
      ]),
      repeater('columns', 'Columns', columnFields(), 'title'),
      group(
        'Selection',
        [
          select('mode', 'Mode', ['add', 'remove']),
          formFieldSelect('targetField', 'Target Field', formBuilderService),
          formFieldSelect(
            'sourceField',
            'Remove Source Field',
            formBuilderService
          ),
          input('actionIcon', 'Action Icon'),
          input('actionLabel', 'Action Label'),
          select('displayMode', 'Action Display', [
            'icon-only',
            'icon-text',
            'text-only',
          ]),
          select('buttonStyle', 'Button Style', ['default', 'icon-button']),
          input('minItems', 'Minimum Items', { type: 'number', min: 0 }),
          input('maxItems', 'Maximum Items', { type: 'number', min: 0 }),
          toggle('preventDuplicates', 'Prevent Duplicates'),
        ],
        'selectionConfig'
      ),
      group('Request', [
        input('url', 'API URL'),
        input('endpointCode', 'Endpoint Code'),
        toggle('useBaseUrl', 'Use API Gateway Base URL'),
        input('debounceTime', 'Debounce Time (ms)', { type: 'number', min: 0 }),
      ]),
      datasetFields(false, true),
      errorMappingFields(),
    ],
    recordPaths: ['dataset.params', 'dataset.headers'],
  },
  [EIremboFormlyFieldTypes.custommultiselectdatafetch]: {
    propKeys: [
      'inputFields',
      'columns',
      'selectionKey',
      'tableTitle',
      'tableDescription',
      'emptyMessage',
      'validateButtonLabel',
      'previewMetadata',
      'url',
      'endpointCode',
      'useBaseUrl',
      'dataPath',
      'paginationConfig',
      'usePrefetch',
      'preFetchChecks',
      'showGlobalError',
      'errorMapping',
      'disabled',
    ],
    fields: [
      group('Results', [
        input('selectionKey', 'Selection Key'),
        input('tableTitle', 'Table Title'),
        textarea('tableDescription', 'Table Description'),
        input('emptyMessage', 'Empty Message'),
        input('validateButtonLabel', 'Search Button Label'),
        input('dataPath', 'Response Data Path'),
        toggle('usePrefetch', 'Prefill Inputs From Form'),
        toggle('showGlobalError', 'Show Error Globally'),
        toggle('disabled', 'Disabled'),
      ]),
      repeater(
        'inputFields',
        'Search Inputs',
        [
          input('key', 'Key', { required: true }),
          input('label', 'Label', { required: true }),
          select('type', 'Type', ['text', 'number', 'email']),
          input('placeholder', 'Placeholder'),
          input('hint', 'Hint'),
          toggle('required', 'Required'),
          input('pattern', 'Pattern'),
          input('minLength', 'Minimum Length', { type: 'number', min: 0 }),
          input('maxLength', 'Maximum Length', { type: 'number', min: 0 }),
          input('defaultValue', 'Default Value'),
          formFieldSelect(
            'sourceField',
            'Prefill Source Field',
            formBuilderService
          ),
          toggle('searchInRootForm', 'Search In Root Form'),
        ],
        'label'
      ),
      repeater('columns', 'Columns', columnFields(), 'title'),
      repeater(
        'previewMetadata',
        'Preview Values',
        [
          input('key', 'Key', { required: true }),
          input('label', 'Label', { required: true }),
          select('type', 'Calculation', [
            'count',
            'sum',
            'first',
            'input',
            'static',
          ]),
          input('sourceField', 'Source Field'),
          input('value', 'Static Value'),
          input('formatter', 'Formatter'),
          input('bindLabel', 'Display Key'),
          input('prefix', 'Prefix'),
          input('suffix', 'Suffix'),
        ],
        'label'
      ),
      group('Request', [
        input('url', 'API URL'),
        input('endpointCode', 'Endpoint Code'),
        toggle('useBaseUrl', 'Use API Gateway Base URL'),
      ]),
      group(
        'Pagination',
        [
          input('pageSize', 'Page Size', { type: 'number', min: 1 }),
          input('pageStart', 'First Page', { type: 'number', min: 0 }),
          input('pageKey', 'Page Parameter'),
          input('sizeKey', 'Size Parameter'),
          input('totalPagesPath', 'Total Pages Path'),
          input('totalElementsPath', 'Total Elements Path'),
        ],
        'paginationConfig'
      ),
      preFetchChecks(formBuilderService),
      errorMappingFields(),
    ],
  },
  [EIremboFormlyFieldTypes.customdirectdatafetch]: {
    propKeys: [
      'url',
      'endpointCode',
      'useBaseUrl',
      'httpMethod',
      'triggerType',
      'genericInputLength',
      'inputLenghtAsMinLength',
      'inputLenghtAsMaxLength',
      'numericOnly',
      'disableInternalFormControl',
      'showGlobalError',
      'successMessage',
      'payloadKey',
      'useMultiplePayloadKeys',
      'payloadKeyMappings',
      'bodyMappings',
      'pathParams',
      'queryParams',
      'populateRelativeToRootForm',
      'populates',
      'errorMapping',
    ],
    fields: [
      ...genericFetchFields(formBuilderService),
      group('Direct Request', [
        select('httpMethod', 'HTTP Method', [
          'GET',
          'POST',
          'PUT',
          'PATCH',
          'DELETE',
        ]),
        directParamMappings(
          'pathParams',
          'Path Parameters',
          formBuilderService
        ),
        directParamMappings(
          'queryParams',
          'Query Parameters',
          formBuilderService
        ),
        directParamMappings(
          'bodyMappings',
          'Body Mappings',
          formBuilderService
        ),
      ]),
    ],
    stripEmptyValuePaths: ['pathParams', 'queryParams', 'bodyMappings'],
  },
  [EIremboFormlyFieldTypes.customgenericguardeddatafetch]: {
    propKeys: [
      'url',
      'endpointCode',
      'useBaseUrl',
      'triggerType',
      'debounceTime',
      'genericInputLength',
      'inputLenghtAsMinLength',
      'inputLenghtAsMaxLength',
      'numericOnly',
      'disableInternalFormControl',
      'showGlobalError',
      'successMessage',
      'payloadKey',
      'useMultiplePayloadKeys',
      'payloadKeyMappings',
      'populateRelativeToRootForm',
      'populates',
      'errorMapping',
      'preFetchChecks',
      'nidVerification',
      'disableFirstFetchOnRFA',
      'transformToArray',
    ],
    fields: [
      ...genericFetchFields(formBuilderService),
      preFetchChecks(formBuilderService),
      group(
        'NID Verification',
        [
          toggle('enabled', 'Enabled'),
          select('idType', 'ID Type', ['NID', 'CHILD_ID', 'NIN']),
          input('endpointCode', 'Endpoint Code'),
          toggle('usePrefetch', 'Prefetch From User Profile'),
          formFieldSelect(
            'nidTypeSource',
            'NID Type Source Field',
            formBuilderService
          ),
        ],
        'nidVerification'
      ),
      group(
        'Transform Response To Array',
        [
          toggle('enabled', 'Enabled'),
          formFieldSelect('targetField', 'Target Field', formBuilderService),
          input('dataPath', 'Response Data Path'),
          toggle('includeInputValues', 'Include Input Values'),
          repeater('mappings', 'Field Mappings', [
            input('sourcePath', 'Source Path', { required: true }),
            input('targetKey', 'Target Key', { required: true }),
            input('formatter', 'Formatter'),
          ]),
          repeater('inputValueMappings', 'Input Value Mappings', [
            formFieldSelect('sourceField', 'Source Field', formBuilderService),
            input('targetKey', 'Target Key', { required: true }),
          ]),
          repeater('columns', 'Preview Columns', columnFields(), 'title'),
          repeater(
            'previewMetadata',
            'Preview Values',
            [
              input('key', 'Key', { required: true }),
              input('label', 'Label', { required: true }),
              select('type', 'Calculation', [
                'count',
                'sum',
                'first',
                'input',
                'static',
              ]),
              input('sourceField', 'Source Field'),
              input('value', 'Static Value'),
              input('formatter', 'Formatter'),
              input('bindLabel', 'Display Key'),
              input('prefix', 'Prefix'),
              input('suffix', 'Suffix'),
            ],
            'label'
          ),
        ],
        'transformToArray'
      ),
      toggle(
        'disableFirstFetchOnRFA',
        'Disable First Fetch During Request For Action'
      ),
    ],
  },
});

const getAtPath = (source: Record<string, any>, path: string): any =>
  path.split('.').reduce((value, key) => value?.[key], source);

const setAtPath = (
  target: Record<string, any>,
  path: string,
  value: any
): void => {
  const parts = path.split('.');
  const last = parts.pop();
  if (!last) return;
  const parent = parts.reduce((current, key) => {
    current[key] = current[key] || {};
    return current[key];
  }, target);
  parent[last] = value;
};

const clone = <T>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));

export const ADVANCED_FORM_FIELD_TYPES: EIremboFormlyFieldTypes[] = [
  EIremboFormlyFieldTypes.customlabelvaluerepeater,
  EIremboFormlyFieldTypes.customconditionaldatafetch,
  EIremboFormlyFieldTypes.customimage,
  EIremboFormlyFieldTypes.genericbuttonfetch,
  EIremboFormlyFieldTypes.customeditableinput,
  EIremboFormlyFieldTypes.customcascadingeditableinput,
  EIremboFormlyFieldTypes.customselfieverification,
  EIremboFormlyFieldTypes.customparagraph,
  EIremboFormlyFieldTypes.customeditabletable,
  EIremboFormlyFieldTypes.customdropdowndatafetchpaginated,
  EIremboFormlyFieldTypes.externalfileupload,
  EIremboFormlyFieldTypes.custominternalfileupload,
  EIremboFormlyFieldTypes.customitemselectiontable,
  EIremboFormlyFieldTypes.custommultiselectdatafetch,
  EIremboFormlyFieldTypes.customdirectdatafetch,
  EIremboFormlyFieldTypes.customgenericguardeddatafetch,
];

export class AdvancedFormlyFieldConfiguration extends BaseFormlyFieldConfiguration {
  private readonly definition: IAdvancedFieldDefinition;

  constructor(injector: Injector, field: FormlyFieldConfig) {
    super(injector, field);
    const definition = definitions(this.formBuilderService)[
      field.type as EIremboFormlyFieldTypes
    ];
    if (!definition) {
      throw new Error(`No advanced configuration for ${field.type}`);
    }
    this.definition = definition;
    this.settingsFormlyForm = [
      ...getDefaultBaseSettingsConfigFormly(this.formBuilderService),
      ...definition.fields,
    ];
  }

  override parseFieldConfigIntoSettingsFormModel(): TConfigurationFormModelTypes {
    const model: Record<string, any> = {
      ...baseFieldParseIntoSettingsFormModel(this.field),
    };
    this.definition.propKeys.forEach(key => {
      model[key] = clone(this.field.props?.[key]);
    });
    this.definition.stringArrayPaths?.forEach(path => {
      const value = getAtPath(model, path);
      if (Array.isArray(value)) setAtPath(model, path, value.join(', '));
    });
    this.definition.recordPaths?.forEach(path => {
      const value = getAtPath(model, path);
      if (value && !Array.isArray(value) && typeof value === 'object') {
        setAtPath(
          model,
          path,
          Object.entries(value).map(([key, entryValue]) => ({
            key,
            value: entryValue,
          }))
        );
      }
    });
    return model as TConfigurationFormModelTypes;
  }

  override updateFieldConfigFromSettingsFormModel(model: any) {
    this.field = baseFieldUpdateFromSettingsFormModel(model, this.field);
    this.field.props = this.field.props || {};
    const normalizedModel = clone(model) as Record<string, any>;
    this.definition.stringArrayPaths?.forEach(path => {
      const value = getAtPath(normalizedModel, path);
      if (typeof value === 'string') {
        setAtPath(
          normalizedModel,
          path,
          value
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
        );
      }
    });
    this.definition.recordPaths?.forEach(path => {
      const value = getAtPath(normalizedModel, path);
      if (Array.isArray(value)) {
        setAtPath(
          normalizedModel,
          path,
          Object.fromEntries(
            value
              .filter(entry => entry?.key)
              .map(entry => [entry.key, entry.value])
          )
        );
      }
    });
    this.definition.stripEmptyValuePaths?.forEach(path => {
      const value = getAtPath(normalizedModel, path);
      if (Array.isArray(value)) {
        value.forEach(entry => {
          if (entry?.value === '') delete entry.value;
        });
      }
    });
    this.definition.propKeys.forEach(key => {
      const value = normalizedModel[key];
      if (value === undefined || value === '') {
        delete this.field.props?.[key];
      } else {
        this.field.props![key] = value;
      }
    });
    return this.field;
  }
}
